import { scenarioMaps } from '../../../data/maps';
import { getProductIconUrl, PRODUCT_ICONS } from '../../../types/contants';

export type SessionTypeDisplay = {
  productType: keyof typeof PRODUCT_ICONS;
  productLabel: string;
  mappedLabel: string | null;
  scenarioLabel: string | null;
  iconUrl: string;
};

type SessionTypeDisplayConfig = Omit<
  SessionTypeDisplay,
  'iconUrl' | 'scenarioLabel'
>;

const SESSION_TYPE_DISPLAY: Record<string, SessionTypeDisplayConfig> = {
  body_image: {
    productType: 'exposure',
    productLabel: 'Augmented Exposure',
    mappedLabel: 'Body Image',
  },
  exposure: {
    productType: 'exposure',
    productLabel: 'Augmented Exposure',
    mappedLabel: 'Food Exposure',
  },
  performance: {
    productType: 'performance',
    productLabel: 'Augmented Performance',
    mappedLabel: 'Performance',
  },
  wellbeing: {
    productType: 'wellbeing',
    productLabel: 'Augmented Experience',
    mappedLabel: 'Wellbeing',
  },
  psychology: {
    productType: 'psychology',
    productLabel: 'Augmented Experience',
    mappedLabel: 'Psychology',
  },
  corporate: {
    productType: 'corporate',
    productLabel: 'Augmented Experience',
    mappedLabel: 'Corporate',
  },
  situations: {
    productType: 'situations',
    productLabel: 'Augmented Experience',
    mappedLabel: 'Situations',
  },
  mentalization: {
    productType: 'mentalization',
    productLabel: 'Augmented Experience',
    mappedLabel: 'Mentalization',
  },
  experience: {
    productType: 'wellbeing',
    productLabel: 'Augmented Experience',
    mappedLabel: null,
  },
  esperienza: {
    productType: 'wellbeing',
    productLabel: 'Augmented Experience',
    mappedLabel: null,
  },
};

const IGNORED_HINTS = new Set([
  '',
  '-',
  'experience',
  'esperienza',
  'esperienza-vr',
  'n/a',
]);

const EXPERIENCE_HINTS: Array<{
  test: (value: string) => boolean;
  mappedLabel: string;
  productType: keyof typeof PRODUCT_ICONS;
}> = [
  {
    test: value => /body[_\s-]?image/.test(value),
    mappedLabel: 'Body Image',
    productType: 'exposure',
  },
  {
    test: value =>
      /food/.test(value) ||
      value === 'exposure' ||
      value === 'augmented exposure',
    mappedLabel: 'Food Exposure',
    productType: 'exposure',
  },
  {
    test: value => /wellbeing/.test(value),
    mappedLabel: 'Wellbeing',
    productType: 'wellbeing',
  },
  {
    test: value => /psychology/.test(value),
    mappedLabel: 'Psychology',
    productType: 'psychology',
  },
  {
    test: value => /corporate/.test(value),
    mappedLabel: 'Corporate',
    productType: 'corporate',
  },
  {
    test: value => /situation/.test(value),
    mappedLabel: 'Situations',
    productType: 'situations',
  },
  {
    test: value => /mentalization/.test(value),
    mappedLabel: 'Mentalization',
    productType: 'mentalization',
  },
  {
    test: value => /performance/.test(value),
    mappedLabel: 'Performance',
    productType: 'performance',
  },
];

const normalizeHint = (value?: string | null): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (IGNORED_HINTS.has(trimmed.toLowerCase())) return null;
  return trimmed;
};

const resolveHint = (
  hint?: string | null
): { mappedLabel: string; productType: keyof typeof PRODUCT_ICONS } | null => {
  const normalized = normalizeHint(hint);
  if (normalized == null) return null;

  const comparable = normalized.toLowerCase().replace(/[_-]+/g, ' ');
  const match = EXPERIENCE_HINTS.find(item => item.test(comparable));
  if (match) {
    return { mappedLabel: match.mappedLabel, productType: match.productType };
  }

  if (normalized.startsWith('Augmented ')) {
    return {
      mappedLabel: normalized.slice('Augmented '.length),
      productType: 'wellbeing',
    };
  }

  return { mappedLabel: normalized, productType: 'wellbeing' };
};

const resolveKnownMapLabel = (
  maps: { value: string; label: string }[] | undefined,
  scenario: string | null | undefined
): string | null => {
  const normalized = normalizeHint(scenario);
  if (normalized == null) {
    return null;
  }

  const option = maps?.find(
    item => item.value.toLowerCase() === normalized.toLowerCase()
  );
  return option?.label ?? null;
};

const resolvePerformanceMappedLabel = (scenario?: string | null): string =>
  resolveKnownMapLabel(scenarioMaps.performance, scenario) ?? 'Performance';

const resolveExposureScenarioLabel = (scenario?: string | null): string | null =>
  resolveKnownMapLabel(scenarioMaps.exposure, scenario);

export const getSessionTypeDisplay = (
  type?: string | null,
  scenario?: string | null,
  source?: string | null
): SessionTypeDisplay => {
  const mapped = type ? SESSION_TYPE_DISPLAY[type] : undefined;
  const hint = resolveHint(source) ?? resolveHint(scenario);

  if (mapped) {
    const isExperienceFamily = type === 'experience' || type === 'esperienza';
    const mappedLabel =
      type === 'performance'
        ? resolvePerformanceMappedLabel(scenario)
        : isExperienceFamily
          ? (hint?.mappedLabel ?? mapped.mappedLabel)
          : mapped.mappedLabel;

    const productType =
      isExperienceFamily && hint?.productType
        ? hint.productType
        : mapped.productType;

    return {
      ...mapped,
      productType,
      mappedLabel,
      scenarioLabel:
        type === 'exposure' ? resolveExposureScenarioLabel(scenario) : null,
      iconUrl: getProductIconUrl(productType),
    };
  }

  if (hint) {
    return {
      productType: hint.productType,
      productLabel: 'Augmented Experience',
      mappedLabel: hint.mappedLabel,
      scenarioLabel: null,
      iconUrl: getProductIconUrl(hint.productType),
    };
  }

  const fallbackType =
    type && type in PRODUCT_ICONS
      ? (type as keyof typeof PRODUCT_ICONS)
      : 'wellbeing';

  const fallbackLabel = normalizeHint(type);
  return {
    productType: fallbackType,
    productLabel: fallbackLabel ?? 'N/A',
    mappedLabel: null,
    scenarioLabel: null,
    iconUrl: getProductIconUrl(fallbackType),
  };
};

export const getSessionDomainLabel = (
  type?: string | null,
  scenario?: string | null,
  source?: string | null
): string => {
  const display = getSessionTypeDisplay(type, scenario, source);
  return display.mappedLabel ?? display.productLabel;
};

export const getSessionDomainKey = (
  type?: string | null,
  scenario?: string | null,
  source?: string | null
): string => {
  const display = getSessionTypeDisplay(type, scenario, source);
  return `${display.productType}::${display.mappedLabel ?? display.productLabel}`;
};

export const getSessionTypeFilterLabel = (type: string): string => {
  const display = getSessionTypeDisplay(type);
  if (type === 'experience' || type === 'esperienza') {
    return display.productLabel;
  }
  return display.mappedLabel ?? display.productLabel;
};
